import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para la integración con Google Calendar (M2.4).
 *
 * Réplica casi byte-por-byte del patrón usado en `./strava.ts` para mantener
 * coherencia entre integraciones OAuth (Strava M1.9, Google M2.4):
 *
 *  - `fetch` directo con `credentials: 'include'` en lugar del helper genérico
 *    `request()` de `./api`, porque cada integración tiene su propio mapeo de
 *    códigos de error (409 = no conectado, 503 con varias causas, etc.).
 *  - El flujo OAuth se hace con navegación real del navegador (no XHR), porque
 *    el backend redirige a `accounts.google.com` y los redirects cross-site no
 *    son seguibles por `fetch`. Ver `startGoogleOAuth()`.
 *  - Tokens cifrados y `client_id` + `client_secret` por profile (igual que
 *    Strava M1.9): el usuario crea su propia app en Google Cloud Console y
 *    pega las claves; nunca compartimos un app global.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Errores específicos de Google
// ---------------------------------------------------------------------------

/** Backend no tiene claves OAuth de Google configuradas para este profile. */
export class GoogleNotConfiguredError extends ApiError {
  public readonly hint: string;
  constructor(hint: string) {
    super('Google no configurado en el backend', 503);
    this.name = 'GoogleNotConfiguredError';
    this.hint = hint;
  }
}

/** Google rechazó el token (401). El frontend debe forzar reconexión. */
export class GoogleUnauthorizedError extends ApiError {
  constructor() {
    super('google_unauthorized', 401);
    this.name = 'GoogleUnauthorizedError';
  }
}

/** Google devolvió rate limit (503 con `google_rate_limited`). */
export class GoogleRateLimitedError extends ApiError {
  public readonly retryAfterSeconds: number | null;
  constructor(retryAfterSeconds: number | null) {
    super('google_rate_limited', 503);
    this.name = 'GoogleRateLimitedError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Google devolvió 5xx (502 desde nuestro backend). */
export class GoogleUpstreamError extends ApiError {
  constructor() {
    super('google 5xx', 502);
    this.name = 'GoogleUpstreamError';
  }
}

/** Usuario no tiene Google conectado al pedir push (409). */
export class GoogleNotConnectedError extends ApiError {
  constructor() {
    super('no conectado', 409);
    this.name = 'GoogleNotConnectedError';
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const GoogleStatusSchema = z.object({
  appConfigured: z.boolean(),
  connected: z.boolean(),
  accountEmail: z.string().optional(),
  lastPushAt: z.string().nullable().optional(),
  lastPushCount: z.number().int().nonnegative().nullable().optional(),
  calendarId: z.string().nullable().optional(),
  // Permite al backend exponer si el push automático está activo (decisión UX
  // documentada abajo: toggle opcional persistido en una preferencia del
  // profile). Por defecto false.
  autoPushOnPlan: z.boolean().optional(),
});
export type GoogleStatus = z.infer<typeof GoogleStatusSchema>;

export const GooglePushResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  deleted: z.number().int().nonnegative(),
  pushedAt: z.string(),
});
export type GooglePushResponse = z.infer<typeof GooglePushResponseSchema>;

export const GooglePushRequestSchema = z.object({
  fromISO: z.string().optional(),
  toISO: z.string().optional(),
});
export type GooglePushRequest = z.infer<typeof GooglePushRequestSchema>;

// ---------------------------------------------------------------------------
// App config — claves Google OAuth por profile, guardadas cifradas en BD
// ---------------------------------------------------------------------------

/**
 * Respuesta de `GET /api/google/app-config`. Si `configured: false` el resto
 * de campos relativos a la app (clientId, updatedAt) no vienen, pero
 * `callbackUrl` SÍ — la UI lo muestra siempre para que el usuario lo pegue
 * en "Authorized redirect URIs" en Google Cloud Console.
 *
 * NUNCA incluye `clientSecret` — sólo el clientId, que es público.
 */
export const GoogleAppConfigSchema = z.object({
  configured: z.boolean(),
  clientId: z.string().optional(),
  callbackUrl: z.string().url(),
  updatedAt: z.string().optional(),
});
export type GoogleAppConfig = z.infer<typeof GoogleAppConfigSchema>;

/**
 * Body para `PUT /api/google/app-config`. Validación replicada del backend:
 *
 *  - `clientId`: Google OAuth client IDs para "Web application" siempre
 *    terminan en `.apps.googleusercontent.com` y empiezan por dígitos,
 *    p.ej. `123456789012-abc123def456ghi789.apps.googleusercontent.com`.
 *  - `clientSecret`: cadena base64-url-safe de longitud variable (Google la
 *    devuelve con 24-40 chars típicamente). Aceptamos 20-100 chars con
 *    `[A-Za-z0-9_-]` para tener tolerancia futura sin abrirlo del todo.
 */
export const GoogleAppConfigPutBody = z.object({
  clientId: z
    .string()
    .min(1, 'Obligatorio')
    .max(150, 'Máximo 150 caracteres')
    .regex(
      /^\d+-[a-z0-9]+\.apps\.googleusercontent\.com$/,
      'Formato esperado: 1234-xxx.apps.googleusercontent.com',
    ),
  clientSecret: z
    .string()
    .min(20, 'Mínimo 20 caracteres')
    .max(100, 'Máximo 100 caracteres')
    .regex(/^[A-Za-z0-9_-]+$/, 'Sólo letras, números, guion y guion bajo'),
});
export type GoogleAppConfigPutBody = z.infer<typeof GoogleAppConfigPutBody>;

/**
 * Respuesta de `PUT /api/google/app-config`. Mismo shape que el GET más
 * `previousConnectionInvalidated`: true cuando el upsert eliminó tokens
 * OAuth previos porque cambió el clientSecret y los tokens viejos ya no
 * sirven.
 */
export const GoogleAppConfigPutResponseSchema = GoogleAppConfigSchema.extend({
  previousConnectionInvalidated: z.boolean().optional(),
});
export type GoogleAppConfigPutResponse = z.infer<typeof GoogleAppConfigPutResponseSchema>;

/**
 * Error de validación 400 del backend: `{ error, issues: { fieldErrors, formErrors } }`
 * tal y como devuelve `z.flatten()`. Lo modelamos aquí para que el form pueda
 * pintar `issues.fieldErrors.clientId[0]` directamente.
 */
export class GoogleAppConfigValidationError extends ApiError {
  public readonly fieldErrors: Record<string, string[]>;
  public readonly formErrors: string[];
  constructor(fieldErrors: Record<string, string[]>, formErrors: string[]) {
    super('Validación rechazada por el backend', 400);
    this.name = 'GoogleAppConfigValidationError';
    this.fieldErrors = fieldErrors;
    this.formErrors = formErrors;
  }
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function pickString(body: unknown, key: string): string | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

function pickNumber(body: unknown, key: string): number | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function getGoogleStatus(): Promise<GoogleStatus> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/google/status`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = GoogleStatusSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError('Respuesta /api/google/status no cumple esquema', response.status, parsed.error);
  }
  return parsed.data;
}

export async function pushGoogle(body?: GooglePushRequest): Promise<GooglePushResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/google/push`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body ?? {}),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = GooglePushResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError('Respuesta /api/google/push no cumple esquema', 200, parsed.error);
    }
    return parsed.data;
  }

  const json = await readJsonSafe(response);
  const errStr = pickString(json, 'error') ?? '';

  if (response.status === 409) {
    throw new GoogleNotConnectedError();
  }
  if (response.status === 401 || errStr === 'google_unauthorized') {
    throw new GoogleUnauthorizedError();
  }
  if (response.status === 503) {
    if (errStr === 'google_rate_limited') {
      const retryAfter =
        pickNumber(json, 'retryAfter') ??
        (Number.parseInt(response.headers.get('Retry-After') ?? '', 10) || null);
      throw new GoogleRateLimitedError(Number.isFinite(retryAfter as number) ? (retryAfter as number) : null);
    }
    if (errStr === 'google no configurado' || errStr.includes('configurado')) {
      const hint = pickString(json, 'hint') ?? '';
      throw new GoogleNotConfiguredError(hint);
    }
  }
  if (response.status === 502) {
    throw new GoogleUpstreamError();
  }

  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

export async function getGoogleAppConfig(): Promise<GoogleAppConfig> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/google/app-config`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = GoogleAppConfigSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta /api/google/app-config no cumple esquema',
      response.status,
      parsed.error,
    );
  }
  return parsed.data;
}

export async function putGoogleAppConfig(
  body: GoogleAppConfigPutBody,
): Promise<GoogleAppConfigPutResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/google/app-config`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }

  if (response.status === 200) {
    const json = await readJsonSafe(response);
    const parsed = GoogleAppConfigPutResponseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(
        'Respuesta /api/google/app-config (PUT) no cumple esquema',
        200,
        parsed.error,
      );
    }
    return parsed.data;
  }

  if (response.status === 400) {
    const json = await readJsonSafe(response);
    const issues =
      json && typeof json === 'object' && 'issues' in json
        ? (json as { issues?: unknown }).issues
        : null;
    const fieldErrors: Record<string, string[]> = {};
    const formErrors: string[] = [];
    if (issues && typeof issues === 'object') {
      const i = issues as { fieldErrors?: unknown; formErrors?: unknown };
      if (i.fieldErrors && typeof i.fieldErrors === 'object') {
        for (const [k, v] of Object.entries(i.fieldErrors as Record<string, unknown>)) {
          if (Array.isArray(v)) {
            fieldErrors[k] = v.filter((x): x is string => typeof x === 'string');
          }
        }
      }
      if (Array.isArray(i.formErrors)) {
        for (const v of i.formErrors) {
          if (typeof v === 'string') formErrors.push(v);
        }
      }
    }
    throw new GoogleAppConfigValidationError(fieldErrors, formErrors);
  }

  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

export async function deleteGoogleAppConfig(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/google/app-config`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  // 204 esperado; 200 también lo aceptamos por defensa. Idempotente: si ya no
  // había nada el backend también devuelve 204.
  if (response.status === 204 || response.ok) return;
  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

export async function disconnectGoogle(): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/google/disconnect`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status === 204 || response.ok) return;
  throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
}

/**
 * Inicia el flujo OAuth de Google. NO es una llamada XHR: navega de verdad,
 * porque el backend hace `302 → accounts.google.com` y los redirects
 * cross-site no funcionan vía `fetch`.
 *
 * Idéntico patrón a `startStravaOAuth()`: asignación a `location.href` para
 * que sea spyable en jsdom (no usar `location.assign(...)`).
 */
export function startGoogleOAuth(): void {
  window.location.href = '/api/google/oauth/start';
}

/**
 * URL del endpoint que devuelve el .ics de los próximos 30 días. La UI la
 * usa como `href` de un `<a download>` — la cookie de sesión viaja igual
 * que con cualquier navegación.
 */
export const GOOGLE_ICS_EXPORT_URL = '/api/calendar/export.ics';

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const GOOGLE_STATUS_QUERY_KEY = ['google', 'status'] as const;
export const GOOGLE_APP_CONFIG_QUERY_KEY = ['google', 'app-config'] as const;

export function useGoogleStatus(): UseQueryResult<GoogleStatus, Error> {
  return useQuery<GoogleStatus, Error>({
    queryKey: GOOGLE_STATUS_QUERY_KEY,
    queryFn: getGoogleStatus,
    staleTime: 0,
    retry: 1,
  });
}

export function useGoogleAppConfig(): UseQueryResult<GoogleAppConfig, Error> {
  return useQuery<GoogleAppConfig, Error>({
    queryKey: GOOGLE_APP_CONFIG_QUERY_KEY,
    queryFn: getGoogleAppConfig,
    staleTime: 0,
    retry: 1,
  });
}

export function useUpdateGoogleAppConfig(): UseMutationResult<
  GoogleAppConfigPutResponse,
  Error,
  GoogleAppConfigPutBody
> {
  const qc = useQueryClient();
  return useMutation<GoogleAppConfigPutResponse, Error, GoogleAppConfigPutBody>({
    mutationFn: (body) => putGoogleAppConfig(body),
    onSuccess: async () => {
      // Cambia tanto app-config como status (que ahora incluye appConfigured);
      // si se invalidó conexión previa también afecta a connected/accountEmail.
      await qc.invalidateQueries({ queryKey: GOOGLE_APP_CONFIG_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: GOOGLE_STATUS_QUERY_KEY });
    },
  });
}

export function useDeleteGoogleAppConfig(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => deleteGoogleAppConfig(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: GOOGLE_APP_CONFIG_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: GOOGLE_STATUS_QUERY_KEY });
    },
  });
}

export function usePushGoogle(): UseMutationResult<
  GooglePushResponse,
  Error,
  GooglePushRequest | undefined
> {
  const qc = useQueryClient();
  return useMutation<GooglePushResponse, Error, GooglePushRequest | undefined>({
    mutationFn: (body) => pushGoogle(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: GOOGLE_STATUS_QUERY_KEY });
    },
  });
}

export function useDisconnectGoogle(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => disconnectGoogle(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: GOOGLE_STATUS_QUERY_KEY });
    },
  });
}
